<template>
  <v-card flat tile class="budget-landing">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                attach=".budget-landing"
                @cancel="closeHelp">
      <budgets-landing-help :openSection="openHelpSection"></budgets-landing-help>
    </res-dialog>
    <res-dialog :showDialog="deletingBudgetIterationId"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                title="Are you sure?"
                cancelButtonText="Cancel"
                cancelButtonColor="resBlue darken-1"
                saveButtonText="YES, DELETE"
                saveButtonColor="resRed darken-1"
                :setWidth="800"
                attach=".budget-landing"
                @save="confirmDeleteBudget"
                @cancel="deletingBudgetIterationId = null">
      <br/>Deleting a budget can not be undone. Do you wish to proceed?
    </res-dialog>
    <v-card-text>
      <res-alert v-model="alertText"></res-alert>
      <v-container class="pa-0">
        <v-row align="end">
          <v-col class="d-flex" cols="12" sm="6">
            <div class="d-flex flex-column flex-grow-1">
              <label>Property</label>
              <property-selector :value="property.value" @input="reloadBudgetsForProperty" :includePropertyGroups="true"></property-selector>
            </div>
            <info-dialog v-if="showInfoDialog"
                         :showDialog="showInfoDialog"
                         :message="infoDialogMessage"
                         :title="infoDialogTitle"
                         @cancelError="cancelInfoDialog">
            </info-dialog>
          </v-col>
          <v-col class="d-flex justify-end" cols="12" sm="6">
            <v-btn rounded elevation="0" outlined color="resBlue darken-1" @click.stop="openImportBudgetDialog">Import</v-btn>
            <span class="px-2"></span>
            <v-btn rounded elevation="0" class="white--text" color="resGreen" @click.stop="openNewEditBudgetDialog">+ New Budget</v-btn>
            <div class="icon-drop" @click="openPaging" title="Pagination Settings">
              <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-cog</v-icon>
            </div>
            <span class="px-2"></span>
            <div class="help-controls step-down" @click="openHelp(null)">
              <span class="help-label">Help</span>
              <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-help-circle</v-icon>
            </div>
            <new-edit-budget-dialog v-if="showNewEditBudgetDialog"
                                    :showDialog="showNewEditBudgetDialog"
                                    :maxWidth="1200"
                                    :setWidth="1200"
                                    :selectedPropertyId="property.value"
                                    :selectedPropertyName="property.text"
                                    @save="createBudget"
                                    @cancel="closeNewEditBudgetDialog">
            </new-edit-budget-dialog>
            <export-budget-dialog v-if="showExportBudgetDialog" :showDialog="showExportBudgetDialog"
                                  :maxWidth="650"
                                  :setWidth="650"
                                  :budgetIterationId="budgetIterationIdToExport"
                                  @cancel="closeExportDialog"
                                  @save="confirmExportVersion">
            </export-budget-dialog>
            <import-budget-dialog v-if="showImportBudgetDialog" :showDialog="showImportBudgetDialog"
                                  :maxWidth="775"
                                  :setWidth="775"
                                  @cancel="closeImportDialogs"
                                  @next="determineNextStepImport">
            </import-budget-dialog>
            <import-budget-exists-dialog v-if="showImportExistingBudgetDialog" :showDialog="showImportExistingBudgetDialog"
                                         :maxWidth="700"
                                         :setWidth="700"
                                         :budgetId="budgetToImport.budgetId"
                                         @cancel="closeImportDialogs"
                                         @confirm="confirmImportExistingVersion">
            </import-budget-exists-dialog>
            <publish-budget-dialog v-if="showPublishBudgetDialog"
                                   :showDialog="showPublishBudgetDialog"
                                   @confirm-publish="publishBudget"
                                   @cancel-publish="closePublishBudgetDialog">
            </publish-budget-dialog>
            <status-history-dialog v-if="showBudgetHistoryDialog"
                                   :showDialog="showBudgetHistoryDialog"
                                   :budgetIterationId="budgetIterationToDisplayHistory.budgetIterationId"
                                   :budgetIterationDescription="budgetIterationToDisplayHistory.description"
                                   @close="closeBudgetHistoryDialog">
            </status-history-dialog>
            <confirm-dialog v-if="showSubmitForApprovalDialog"
                            :showDialog="showSubmitForApprovalDialog"
                            :message="confirmSubmitDialogMessage"
                            :title="confirmSubmitDialogTitle"
                            @confirmAgree="submitItemForApproval"
                            @cancelConfirm="closeSubmitForApprovalDialog">
            </confirm-dialog>
            <note-entry-dialog v-if="showDisputeNoteDialog"
                               :showDialog="showDisputeNoteDialog"
                               :title="'dispute'"
                               @save="disputeItem"
                               @cancel="closeDisputeNoteDialog">
            </note-entry-dialog>
            <reforecast-dialog v-if="showReforecastDialog"
                               :showDialog="showReforecastDialog"
                               :budgetYear="reforecastingIteration.year"
                               :budgetStartMonth="reforecastingIteration.startMonth"
                               :maxWidth="950"
                               :setWidth="950"
                               @confirm="confirmReforecast"
                               @cancel="closeReforecastDialog">
            </reforecast-dialog>
            <paging-dialog v-if="showPagingDialog"
                               :showDialog="showPagingDialog"
                               :maxWidth="600"
                               :setWidth="600"
                               @confirm="savePagingDialog"
                               @cancel="cancelPagingDialog">
            </paging-dialog>
          </v-col>
        </v-row>
        <v-row class="pb-2">
          <v-col class="d-flex" cols="12" sm="2">
            <div class="d-flex flex-column flex-grow-1">
              <label>Year</label>
              <budgets-year-selector :readonly="!propertyIsSelected" :value="year" :triggerLoad="triggerYearLoad" @input="reloadBudgetsForYear"></budgets-year-selector>
            </div>
          </v-col>
          <v-col class="d-flex" cols="12" sm="2">
            <div class="d-flex flex-column flex-grow-1">
              <label>Basis</label>
              <budgets-accounting-basis-selector :readonly="!propertyIsSelected" :value="basis.value" :triggerLoad="triggerBasisLoad" @input="reloadBudgetsForBasis"></budgets-accounting-basis-selector>
            </div>
          </v-col>
        </v-row>
      </v-container>
      <v-container class="pa-0 budget-tabs-container">
        <v-card outlined>
          <v-row>
            <v-col class="d-flex" cols="12" sm="12">
              <v-tabs v-model="tab" class="resman-tabs" active-class="resman-active-tab">
                <v-tabs-slider color="resBlue darken-1"></v-tabs-slider>
                <v-tab v-for="item in tabs"
                       :key="item.id" :class="item.class">
                  {{ item.text }}
                </v-tab>
              </v-tabs>
            </v-col>
          </v-row>
        </v-card>
      </v-container>
      <res-data-table :headers="headers"
                      :items="budgetItems"
                      item-key="id"
                      :default-sort-by="sortBy"
                      :default-sort-descending="sortDescending"
                      :slotOptions="['status', 'version', 'lastModifiedTimestamp', 'action']"
                      :loading="loading">
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="getColor(item.status)" dark label small>{{ item.status }}</v-chip>
        </template>
        <template v-slot:[`item.version`]="{ item }">
          <v-chip label
                  outlined small color="var(--v-resGrey-darken1)">{{item.version}}</v-chip>
        </template>
        <template v-slot:[`item.lastModifiedTimestamp`]="{ item }">
          {{ localizeDate(item.lastModifiedTimestamp) }}
        </template>
        <template v-slot:[`item.action`]="{ item }">
          <div class="d-flex justify-end">
            <v-tooltip bottom v-if="iterationCanBeEdited(item.status, item.isArchived, item.userCanEdit, item.userCanEditApproved, item.userCanEditPublished)">
              <template v-slot:activator="{ on, attrs }">
                <v-btn :to="{ name: 'BaseRent', params: { budgetIterationId: item.budgetIterationId }}"
                       v-bind="attrs" v-on="on" icon color="var(--v-resBlue-darken1)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
            <v-tooltip bottom v-if="!iterationCanBeEdited(item.status, item.isArchived, item.userCanEdit, item.userCanEditApproved, item.userCanEditPublished)">
              <template v-slot:activator="{ on, attrs }">
                <v-btn :to="{ name: 'BaseRent', params: { budgetIterationId: item.budgetIterationId }}"
                       v-bind="attrs" v-on="on" icon color="var(--v-resBlue-darken1)">
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>View</span>
            </v-tooltip>
            <v-menu offset-y close-on-click>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon color="var(--v-resBlue-darken1)">
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item v-if="iterationCanBeCloned(item.status)" @click="cloneItem(item)">
                  <v-list-item-title>Clone</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="iterationCanBeExported(item.userCanImportExport)" @click="exportItem(item)">
                  <v-list-item-title>Export</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item v-if="iterationCanBeSubmitedForApproval(item.status, item.isArchived, isNullOrWhitespace(item.approvalWorkflowId), item.userCanSubmit)" @click="openSubmitForApprovalDialog(item)">
                  <v-list-item-title>Submit for Approval</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="iterationCanBeApproved(item.status, item.isArchived, isNullOrWhitespace(item.approvalWorkflowId), item.userCanApprove)" @click="approveItem(item)">
                  <v-list-item-title>Approve</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="iterationCanBeDisputed(item.status, item.isArchived, isNullOrWhitespace(item.approvalWorkflowId), item.userCanApprove)" @click="openDisputeNoteDialog(item)">
                  <v-list-item-title>Dispute</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="iterationCanBePublished(item.status, item.isArchived)" @click="openPublishBudgetDialog(item)">
                  <v-list-item-title>Publish</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="iterationCanBeReforecast(item.status, item.isArchived)" @click="reforecastItem(item)">
                  <v-list-item-title>Reforecast</v-list-item-title>
                </v-list-item>
                <v-divider v-if="!item.isArchived"></v-divider>
                <v-list-item @click="openStatusHistoryDialog(item)">
                  <v-list-item-title>Status History</v-list-item-title>
                </v-list-item>
                <v-divider v-if="item.status != 'Published'"></v-divider>
                <v-list-item v-if="iterationCanBeArchived(item.status, item.isArchived)" @click="archiveItem(item, 'true')">
                  <v-list-item-title>Archive</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="iterationCanBeUnarchived(item.isArchived)" @click="archiveItem(item, 'false')">
                  <v-list-item-title>Unarchive</v-list-item-title>
                </v-list-item>
                <v-divider v-if="canDelete(item.isArchived, tab)"></v-divider>
                <v-list-item v-if="canDelete(item.isArchived, tab)" @click="deleteItem(item)">Delete</v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </res-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
  import ResDataTable from '@/components/base/ResDataTable.vue';
  import InfoDialog from '@/components/base/dialogs/InfoDialog.vue';
  import ResDialog from '@/components/base/ResDialog.vue';
  import ConfirmDialog from '@/components/base/dialogs/ConfirmDialog.vue';
  import NoteEntryDialog from '@/components/base/dialogs/NoteEntryDialog.vue';
  import NewEditBudgetDialog from '../dialogs/NewEditBudgetDialog.vue';
  import PropertySelector from '@/components/properties/PropertySelector.vue';
  import BudgetsYearSelector from './BudgetsYearSelector.vue';
  import BudgetsAccountingBasisSelector from './BudgetsAccountingBasisSelector.vue';
  import ExportBudgetDialog from '../dialogs/ExportBudgetDialog.vue';
  import ImportBudgetDialog from '../dialogs/ImportBudgetDialog.vue';
  import ImportBudgetExistsDialog from '../dialogs/ImportExistingBudgetDialog.vue';
  import PublishBudgetDialog from '../dialogs/PublishBudgetDialog.vue';
  import ReforecastDialog from '../dialogs/ReforecastDialog.vue';
  import StatusHistoryDialog from '../dialogs/StatusHistoryDialog.vue';
  import ResAlert from '@/components/base/ResAlert.vue';
  import PagingDialog from '../dialogs/PagingDialog.vue';

  import BudgetsLandingHelp from '@/components/help/BudgetsLandingHelp.vue';

  import IsNullOrWhitespaceMixin from '@/mixins/isNullOrWhitespace.js';
  import HelpableMixin from '@/mixins/Helpable.js';

  import Localizable from '@/mixins/Localizable.js';

  import UserService from '@/services/users';
  import BudgetService from '@/services/budget';
  import WorkflowService from '@/services/workflow';

  import { mapActions } from 'vuex';
  import { mapGetters } from 'vuex';

  export default {
    name: 'Landing',
    components: {
      ResDataTable,
      InfoDialog,
      ResDialog,
      ConfirmDialog,
      NoteEntryDialog,
      NewEditBudgetDialog,
      PropertySelector,
      BudgetsYearSelector,
      BudgetsAccountingBasisSelector,      
      ExportBudgetDialog,
      ImportBudgetDialog,
      ImportBudgetExistsDialog,
      PublishBudgetDialog,
      ReforecastDialog,
      StatusHistoryDialog,
      ResAlert,
      BudgetsLandingHelp,
      PagingDialog
    },
    data: function () {
      return {        
        infoDialogMessage: '',
        infoDialogTitle: '',
        confirmSubmitDialogMessage: 'Are you sure you want to submit this budget for approval?',
        confirmSubmitDialogTitle: 'Submit for Approval',
        showInfoDialog: false,
        property: { text: '', value: '' },
        year: "",
        basis: { text: '', value: '' },
        sortBy: "year",
        sortDescending: false,
        budgets: [],
        showNewEditBudgetDialog: false,
        showSaveDialog: false,
        showExportBudgetDialog: false,
        showImportBudgetDialog: false,
        showImportExistingBudgetDialog: false,
        showImportAndCreateBudgetDialog: false,
        showSubmitForApprovalDialog: false,
        showDisputeNoteDialog: false,
        showPublishBudgetDialog: false,
        showBudgetHistoryDialog: false,
        showReforecastDialog: false,
        showPagingDialog: false,
        loading: true,
        newBudget: {},
        budgetToExport: {},
        budgetIterationIdToExport: null,
        budgetIterationToAdvanceApprovalWorkflow: null,
        budgetIterationIdToPublish: null,
        budgetIterationModifyingLastModifiedTimestamp: null,
        budgetIterationToDisplayHistory: { description: '', budgetIterationId: '' },
        userGroupMembership: null,
        alertText: {},
        triggerYearLoad: 0,
        triggerBasisLoad: 0,
        tab: null,
        reforecastingDialog: [],
        userHasDeleteArchivedPermission: false,
        deletingBudgetIterationId: null
      };
    },
    mixins: [IsNullOrWhitespaceMixin, HelpableMixin, Localizable],
    computed: {
      ...mapGetters(['currentLoading', 'selectedPropertyId']),
      propertyIsSelected() {
        return this.property && this.property.value && this.property.value.length == 36; /* guid length */
      },
      headers() {
        return this.budgets.length > 0 ? [
          { text: "Property", value: "propertyAbbreviation", sortable: true, filterable: false },
          { text: "Year", value: "year", sortable: true, filterable: false },
          { text: "Basis", value: "accountingBasis", sortable: true, filterable: false },
          { text: "Status", value: "status", sortable: true, filterable: false },
          { text: "Identifier", value: "version", sortable: true, filterable: false },
          { text: "Description", value: "description", sortable: true, filterable: false },
          { text: "Last Modified by", value: "lastModifiedBy", sortable: true, filterable: false },
          { text: "Last Modified", value: "lastModifiedTimestamp", sortable: true, filterable: false },
          { text: "Actions", value: "action", sortable: false, filterable: false }
        ] : [];
      },
      allBudgetsNonArchived() {
        let tempbudgets = this.budgets;
        tempbudgets = tempbudgets.filter((item) => {
          return (!item.isArchived);
        });
        return tempbudgets;
      },
      budgetVersions() {
        let tempbudgets = this.budgets;
        tempbudgets = tempbudgets.filter((item) => {
          return (!item.isArchived && item.version.indexOf("RF") == -1 && item.status != 'Approved' && item.status != 'Published');
        });
        return tempbudgets;
      },
      budgetReforecasts() {
        let tempbudgets = this.budgets;
        tempbudgets = tempbudgets.filter((item) => {
          return (!item.isArchived && item.version.indexOf("RF") !== -1 && item.status != 'Approved' && item.status != 'Published');
        });
        return tempbudgets;
      },
      approvedBudgets() {
        let tempbudgets = this.budgets;
        tempbudgets = tempbudgets.filter((item) => {
          return (!item.isArchived && (item.status == 'Approved' || item.status == 'Published'));
        });
        return tempbudgets;
      },
      archivedBudgets() {
        let tempbudgets = this.budgets;
        tempbudgets = tempbudgets.filter((item) => {
          return (item.isArchived);
        });
        return tempbudgets;
      },
      tabs() {
        return [
          { id: 1, text: 'VIEW ALL (' + this.allBudgetsNonArchived.length + ')', class: '' },
          { id: 2, text: 'VERSIONS (' + this.budgetVersions.length + ')', class: '' },
          { id: 3, text: 'REFORECASTS (' + this.budgetReforecasts.length + ')', class: '' },
          { id: 4, text: 'APPROVED (' + this.approvedBudgets.length + ')', class: '' },
          { id: 5, text: 'ARCHIVED (' + this.archivedBudgets.length + ')', class: 'resman-right-tab' },
        ];
      },
      budgetItems() {
        switch (this.tab) {
          case 0:
            return this.allBudgetsNonArchived;
          case 1:
            return this.budgetVersions;
          case 2:
            return this.budgetReforecasts;
          case 3:
            return this.approvedBudgets;
          case 4:
            return this.archivedBudgets;
          default:
            return this.allBudgetsNonArchived;
        }
      },
    },
    methods: {
      ...mapActions(['changeLoading', 'activateErrorDialog']),
      cancelInfoDialog() {
        this.showInfoDialog = false;
        this.infoDialogTitle = '';
        this.infoDialogMessage = '';
      },
      getColor(status) {
        if (status == 'Open') {
          return 'grey';
        }
        else if (status == 'Approved') {
          return 'green';
        }
        else if (status == 'Published') {
          return 'blue';
        }
        else if (status == 'Disputed') {
          return 'red';
        }
        else {
          return 'orange';
        }
      },
      iterationCanBeEdited(status, isArchived, userCanEdit, userCanEditApproved, userCanEditPublished) {
        return (status == 'Open' || status == 'Disputed' || ((status == 'Approved' || (status || '').toLowerCase().startsWith('pending') || (status || '').toLowerCase() == 'pending approval') && userCanEditApproved) || (status == 'Published' && userCanEditPublished)) && !isArchived && userCanEdit;
      },
      iterationCanBeCloned() {
        return true; // anyone can do it now
      },
      iterationCanBeExported(userCanImportExport) {
        return userCanImportExport; // permissions only?
      },
      iterationCanBeSubmitedForApproval(status, isArchived, workflowIsNull, userCanSubmit) {
        return userCanSubmit && !workflowIsNull && (status == 'Open' || status == 'Disputed') && !isArchived;
      },
      iterationCanBeApproved(status, isArchived, workflowIsNull, userCanApprove) {
        return userCanApprove && ((status != 'Open' && status != 'Disputed') || workflowIsNull) && status != 'Approved' && status != 'Published' && !isArchived;
      },
      iterationCanBeDisputed(status, isArchived, workflowIsNull, userCanApprove) {
        return userCanApprove && !workflowIsNull && status != 'Open' && status != 'Disputed' && status != 'Approved' && status != 'Published' && !isArchived;
      },
      iterationCanBePublished(status, isArchived) {
        return status == 'Approved' && !isArchived;
      },
      iterationCanBeArchived(status, isArchived) {
        return !isArchived && status != 'Published';
      },
      iterationCanBeUnarchived(isArchived) {
        return isArchived;
      },
      iterationCanBeReforecast(status, isArchived) {
        return !isArchived && status == 'Published';
      },
      canDelete(itemIsArchived, tab) {
        return itemIsArchived && tab === 4 && this.userHasDeleteArchivedPermission;
      },
      async openNewEditBudgetDialog() {
        this.showNewEditBudgetDialog = true;
      },
      closeNewEditBudgetDialog() {
        this.showNewEditBudgetDialog = false;
      },
      async createBudget(succeeded) {
        // if save succeeded, reload the budget list and the year dropdown
        if (succeeded) {
          await this.loadBudgets();
          this.triggerYearLoad++;
          this.triggerBasisLoad++;
        }
        this.showNewEditBudgetDialog = false;
      },
      async openImportBudgetDialog() {
        this.budgetToImport = {};
        this.showImportBudgetDialog = true;
      },
      closeImportDialogs() {
        this.showImportBudgetDialog = false;
        this.showImportExistingBudgetDialog = false;
        this.showImportAndCreateBudgetDialog = false;        
      },
      async determineNextStepImport(iBudget) {
        this.loading = true;
        this.budgetToImport = iBudget;
        let budgetExistsResults = await BudgetService.checkIfBudgetExists(this.budgetToImport.property.value, this.budgetToImport.year,
          this.budgetToImport.accountingBasisId);

        // if budget exists, show budget exists dialog but hold onto the details and file info
        if (budgetExistsResults && budgetExistsResults.budgetExists == true) {
          this.budgetToImport.budgetId = budgetExistsResults.budgetId; // this indeed is the base budget ID that all its iterations tie back to
          this.showImportBudgetDialog = false;
          this.showImportExistingBudgetDialog = true;
        } else {     
          // just create a new budget with default import settings instead
          this.showImportBudgetDialog = false;
          await this.confirmImportBudget();
        }

        this.loading = false;
      },
      async confirmImportExistingVersion(budgetIterationId) {
        this.budgetToImport.budgetIterationId = budgetIterationId;
        this.budgetToImport.addNewVersion = !budgetIterationId;
        await this.confirmImportBudget();
      },
      async confirmImportBudget() {
        var self = this;
        self.loading = true;
        self.closeImportDialogs();

        var formData = new FormData();
        formData.append("file", self.budgetToImport.file);

        let result = {
          Success: false, Data: {}
        };
        if (self.budgetToImport.budgetIterationId || self.budgetToImport.addNewVersion) {
          result = await BudgetService.importExistingBudget(formData, self.budgetToImport.property.value, self.budgetToImport.year,
            self.budgetToImport.accountingBasisId, self.budgetToImport.budgetIterationId);
        } else {
          self.budgetToImport.description = "Imported Budget";
          result = await BudgetService.importAndCreateBudget(formData, self.budgetToImport.property.value, self.budgetToImport.year,
            self.budgetToImport.accountingBasisId, self.budgetToImport.startMonth);
        }

        // if save worked, reload the get all and the year dropdown
        if (result.Success) {         
          this.property = self.budgetToImport.property;
          await this.loadBudgets();
          this.triggerYearLoad++;
          this.triggerBasisLoad++;
          self.alertText = 'Budget for ' + self.budgetToImport.property.text + ' imported successfully!';
        } else {
          self.loading = false;
          //this has been handled generically in services/api/index.js
          //this.activateErrorDialog({title: 'Import Budget Failed', message: result.Data});
        }        
      },
      async reloadBudgetsForProperty(property) {
        this.property = property;
        await this.loadBudgets();
      },
      async reloadBudgetsForYear(year) {
        this.year = year;
        await this.loadBudgets();
      },
      async reloadBudgetsForBasis(basis) {
        this.basis = basis;
        await this.loadBudgets();
      },
      async loadBudgets() {
        let propertyId = this.property.value || this.selectedPropertyId;
        if (!propertyId) {
          this.budgets = [];
          return;
        }
        this.loading = true;
        let isPropertyGroup = this.property && this.property.isPropertyGroup;
        let budgetData = await BudgetService.getAll(propertyId, isPropertyGroup, this.year, this.basis.value);
        if (budgetData && budgetData.data) {
          this.budgets = budgetData.data;
        } else {
          this.budgets = [];
        }
        this.loading = false;
      },
      async loadPermissions() {
        var userSettings = await UserService.getUserSettings(true);

        this.userHasDeleteArchivedPermission = userSettings && userSettings.permissions && userSettings.permissions.DeleteArchivedBudgets && userSettings.permissions.DeleteArchivedBudgets.hasPermission;
      },
      async cloneItem(budgetIteration) {
        var self = this;
        self.loading = true;
        var succeeded = await BudgetService.createBudgetIteration(budgetIteration.budgetId, budgetIteration.budgetIterationId, budgetIteration.budgetIterationTypeId);
        if (succeeded) {
          await this.loadBudgets();
        } else {
          self.loading = false;
          //this has been handled generically in services/api/index.js
          //self.activateErrorDialog({ title: 'Clone Failed', message: "Request failed. Please reload and try again." });
        }        
      },
      async exportItem(budgetIteration) {
        this.budgetIterationIdToExport = budgetIteration.budgetIterationId;
        this.showExportBudgetDialog = true;
      },
      closeExportDialog() {
        this.showExportBudgetDialog = false;
      },
      async confirmExportVersion(eBudget) {
          var self = this;
        self.loading = true;
        if (eBudget.selectedExportFormat == 1) {
          let response = await BudgetService.getBudgetExpandedExportURL(eBudget);
          if (response.Success && response.Data && response.Data.length > 0) {
            window.open(response.Data);
            self.closeExportDialog();
            self.loading = false;
          } else {
            self.closeExportDialog();
            self.loading = false;
          }
        }
        else {
          let response = await BudgetService.getBudgetBasicExportURL(eBudget.budgetIterationId);
          if (response.Success && response.Data && response.Data.length > 0) {
            window.open(response.Data);
            self.closeExportDialog();
            self.loading = false;
          } else {

            self.closeExportDialog();
            self.loading = false;
          }
        }
      },
      openStatusHistoryDialog(budgetIteration) {
        this.budgetIterationToDisplayHistory = budgetIteration;
        this.showBudgetHistoryDialog = true;
      },
      closeBudgetHistoryDialog() {
        this.showBudgetHistoryDialog = false;
        this.budgetIterationToDisplayHistory = { description: '', budgetIterationId: '' };
      },
      openSubmitForApprovalDialog(budgetIteration) { // has to confirm but no notes
        this.budgetIterationToAdvanceApprovalWorkflow = budgetIteration.budgetIterationId;
        this.budgetIterationModifyingLastModifiedTimestamp = budgetIteration.lastModifiedTimestamp;
        this.showSubmitForApprovalDialog = true;
      },
      closeSubmitForApprovalDialog() {
        this.showSubmitForApprovalDialog = false;
        this.budgetIterationModifyingLastModifiedTimestamp = null;
        this.budgetIterationToAdvanceApprovalWorkflow = null;
      },      
      async submitItemForApproval() {
        this.changeLoading(true);
        var result = await WorkflowService.submitBudgetForApproval(this.budgetIterationToAdvanceApprovalWorkflow, this.budgetIterationModifyingLastModifiedTimestamp);
        if (!result) {
          // Official error messages?

          //this has been handled generically in services/api/index.js
          //this.activateErrorDialog({ message: 'Something went wrong submitting the budget for approval. Please try again or contact support for further assistance.' });
        } else {
          this.infoDialogTitle = 'Submit for Approval Succeeded';
          this.infoDialogMessage = 'Budget Successfully Submitted!';
          this.showInfoDialog = true;
          await this.loadBudgets();
        }
        this.closeSubmitForApprovalDialog();
        this.changeLoading(false);
      },
      async approveItem(budgetIteration) { // no confirm, no notes
        this.changeLoading(true);

        var result = await WorkflowService.approveBudget(budgetIteration.budgetIterationId, budgetIteration.lastModifiedTimestamp);
        if (!result) {
          // Official error messages?

          //this has been handled generically in services/api/index.js
          //this.activateErrorDialog({ message: 'Something went wrong approving the budget. Please try again or contact support for further assistance.' });
        } else {
          this.infoDialogTitle = 'Approval Succeeded';
          this.infoDialogMessage = 'Budget Successfully Approved!';
          this.showInfoDialog = true;
          await this.loadBudgets();
        }
        this.changeLoading(false);
      },
      openDisputeNoteDialog(budgetIteration) {
        this.budgetIterationToAdvanceApprovalWorkflow = budgetIteration.budgetIterationId;
        this.budgetIterationModifyingLastModifiedTimestamp = budgetIteration.lastModifiedTimestamp;
        this.showDisputeNoteDialog = true;
      },
      closeDisputeNoteDialog() {
        this.showDisputeNoteDialog = false;
        this.budgetIterationToAdvanceApprovalWorkflow = null;
        this.budgetIterationModifyingLastModifiedTimestamp = null;
      },
      async disputeItem(note) { // has to include notes
        this.changeLoading(true);
        var result = await WorkflowService.disputeBudget(this.budgetIterationToAdvanceApprovalWorkflow, note, this.budgetIterationModifyingLastModifiedTimestamp);
        if (!result) {
          // Official error messages?

          //this has been handled generically in services/api/index.js
          //this.activateErrorDialog({ message: 'Something went wrong disputing the budget. Please try again or contact support for further assistance.' });
        } else {
          this.infoDialogTitle = 'Dispute Succeeded';
          this.infoDialogMessage = 'Budget Successfully Disputed!';
          this.showInfoDialog = true;
          await this.loadBudgets();
        }
        this.closeDisputeNoteDialog();
        this.changeLoading(false);
      },
      openPublishBudgetDialog(budgetIteration) {
        this.budgetIterationIdToPublish = budgetIteration.budgetIterationId;
        this.budgetIterationModifyingLastModifiedTimestamp = budgetIteration.lastModifiedTimestamp;
        this.showPublishBudgetDialog = true;
      },
      closePublishBudgetDialog() {
        this.showPublishBudgetDialog = false;
        this.budgetIterationIdToPublish = null;
        this.budgetIterationModifyingLastModifiedTimestamp = null;
      },
      async publishBudget() {
        this.changeLoading(true);
        var result = await BudgetService.publishBudget(this.budgetIterationIdToPublish, this.budgetIterationModifyingLastModifiedTimestamp);
        if (!result) {
          // Official error messages?

          //this has been handled generically in services/api/index.js
          //this.activateErrorDialog({ message: 'Something went wrong publishing the budget. Please try again or contact support for further assistance.' });
        } else {
          this.infoDialogTitle = 'Publish Succeeded';
          this.infoDialogMessage = 'Budget Successfully Published!';
          this.showInfoDialog = true;
          await this.loadBudgets();
        }
        this.closePublishBudgetDialog();
        this.changeLoading(false);
      },
      async archiveItem(budgetIteration, newIsArchived) {
        var self = this;
        self.loading = true;
        var succeeded = await BudgetService.updateIsArchived(budgetIteration.budgetIterationId, newIsArchived, budgetIteration.lastModifiedTimestamp);
        if (succeeded) {
          await this.loadBudgets();
        } else {
          self.loading = false;

          //this has been handled generically in services/api/index.js
          //self.activateErrorDialog({ title: 'Archive/Unarchive Failed', message: "Request failed. Please reload and try again." });
        }  
      },
      deleteItem(budgetIteration) {
        this.deletingBudgetIterationId = budgetIteration.budgetIterationId;
      },
      async confirmDeleteBudget() {
        this.loading = true;
        var succeeded = await BudgetService.delete(this.deletingBudgetIterationId);

        this.deletingBudgetIterationId = null;
        if (succeeded) {
          await this.loadBudgets();
        }
        else {
          this.loading = false;
        }
      },
      reforecastItem(budgetIteration) {
        this.reforecastingIteration = budgetIteration;
        this.openReforecastDialog();

      },
      openReforecastDialog() {
        this.showReforecastDialog = true;
      },
      closeReforecastDialog() {
        this.showReforecastDialog = false;
        this.reforecastingIteration = {};
      },
      async confirmReforecast(e) {
        this.loading = true;
        await BudgetService.reforecastBudget(this.reforecastingIteration.budgetIterationId, e.throughMonthOption, e.populateWithOption, e.deactivateMarketRentAndOccupancy, this.reforecastingIteration.lastModifiedTimestamp);
        this.closeReforecastDialog();
        await this.loadBudgets();
        this.loading = false;
        this.changeLoading(false);
      },
      async getUserGroupMembership() {
      },
      getPageSize() {
        return +(sessionStorage.getItem('budgets-pro-page-size')) || 25;
      },
      savePageSize(pageSize) {
        sessionStorage.setItem('budgets-pro-page-size', pageSize);
      },
      openPaging() {
        this.pageSize = this.getPageSize();
        this.showPagingDialog = true;
      },
      savePagingDialog(ev) {
        this.savePageSize(+ev.pageSize);
        this.showPagingDialog = false;
        this.changeLoading(false);
      },
      cancelPagingDialog() {
        this.pageSize = this.getPageSize();
        this.showPagingDialog = false;
        this.changeLoading(false);
      }
    },
    async mounted() {
      if (this.propertyIsSelected == true) {
        await this.loadBudgets();
      } else {
        this.budgets = [];
      }
      await this.loadPermissions();


      this.loading = false;
    }
  };
</script>

<style lang="scss" scoped>
  .resman-tab {
    color: var(--v-resGrey-darken1) !important;
    font-weight: normal !important;
    font-size: 18px !important;
  }
  .resman-tab.resman-active-tab {
    font-weight: 500 !important;
    color: var(--v-resBlue-darken1) !important;
  }
  .v-data-table-header th.resGrey-background {
    background-color: var(--v-resGrey-lighten3) !important;
  }
  .budget-landing .table-container::v-deep .v-data-table th.res-data-table-actions {
    min-width: 55px !important;
  }
  .budget-tabs-container {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  .resman-right-tab {
    margin-left: auto;
  }
  .help-controls {
    float: right;
    border-radius: 5px;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 7px 6px 6px 6px;
    cursor: pointer;
  }
  .icon-drop {
      margin-top: 6px;
      margin-left:5px;
  }
</style>
